/**
 * useQuickRead Hook
 *
 * Provides a one-click "Quick Read" experience that uses the recommendation
 * system to find the best next story for the student.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { recommendationsApi, QuickRecommendationResponse } from '@/lib/api-client';

export type QuickReadState = 'idle' | 'loading' | 'generating' | 'error';

interface UseQuickReadResult {
  /** Trigger the quick read flow */
  startQuickRead: () => void;
  /** Current state of the quick read process */
  state: QuickReadState;
  /** Error message if state is 'error' */
  error: string | null;
  /** Whether the button should be disabled */
  isDisabled: boolean;
  /** Recommendation data (available after successful fetch) */
  recommendation: QuickRecommendationResponse | null;
}

export function useQuickRead(): UseQuickReadResult {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<QuickReadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<QuickRecommendationResponse | null>(null);

  const quickReadMutation = useMutation({
    mutationFn: () => recommendationsApi.getQuickRecommendation(),
    onMutate: () => {
      setState('loading');
      setError(null);
    },
    onSuccess: (data) => {
      setRecommendation(data);

      if (data.type === 'existing_story' && data.story_id) {
        // Navigate directly to the story
        navigate(`/story/${data.story_id}`);
        setState('idle');

        notifications.show({
          title: 'Starting your story',
          message: data.reason,
          color: 'teal',
        });
      } else if (data.type === 'generate_new' && data.theme_id) {
        // Navigate to story creation with the recommended theme
        setState('generating');
        navigate('/library/story/create', {
          state: {
            themeId: data.theme_id,
            fromQuickRead: true,
          },
        });

        notifications.show({
          title: 'Creating a new story for you',
          message: data.reason,
          color: 'blue',
        });

        // Reset state after navigation
        setTimeout(() => setState('idle'), 1000);
      } else {
        // No recommendation available
        setState('idle');
        notifications.show({
          title: 'No stories available',
          message: data.reason || 'Try generating a new story or check back later.',
          color: 'yellow',
        });
      }
    },
    onError: (err: Error) => {
      setState('error');
      setError(err.message);

      notifications.show({
        title: 'Could not find a recommendation',
        message: err.message || 'Please try again later.',
        color: 'red',
      });
    },
  });

  const startQuickRead = useCallback(() => {
    quickReadMutation.mutate();
  }, [quickReadMutation]);

  return {
    startQuickRead,
    state,
    error,
    isDisabled: state === 'loading' || state === 'generating',
    recommendation,
  };
}
